import {Component, OnInit, Optional} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {ConfiguratorProviderService, ProductTypeEnum, ProviderDTO} from '@tj/tj-dmp-lib-api';
import {ConfirmationService, MenuItem} from 'primeng/api';
import {RemunerationAccordionComponent} from '../../../invoicing/invoice/remuneration-accordion/remuneration-accordion.component';
import {configurationRemunerations} from '../../../invoicing/invoice/remuneration-configuration';

type ProviderInvoicePricing = ProviderDTO & {
    vatValue?: number;
    priceDisplayMode?: 'HT' | 'TTC';
};

@Component({
    selector: 'dmp-provider-edit',
    templateUrl: './provider-edit.component.html',
    styleUrls: ['./provider-edit.component.scss'],
    providers: [ConfirmationService],
    standalone: false
})
export class ProviderEditComponent implements OnInit {
    provider: ProviderInvoicePricing;
    datastore: string;
    breadcrumb: MenuItem[] = [
        {label: 'Paramétrages'},
        {label: 'Fournisseurs', routerLink: '/h/settings/provider'},
        {label: 'Création'}
    ];
    description = 'Création d’un fournisseur';
    readonly civilities = [
        {name: 'Monsieur', code: 'Mr'},
        {name: 'Madame', code: 'Ms'}
    ];
    readonly languageOptions = [
        {label: 'Français', value: 'fr'},
        {label: 'Anglais', value: 'en'}
    ];
    form: FormGroup;
    saving = false;
    submitted = false;
    error = '';
    private selectedProducts: ProductTypeEnum[] = [];
    private selectedLeadProducts: ProductTypeEnum[] = [];

    readonly addressFields = [
        {key: 'address1', label: 'Adresse'}, {key: 'address2', label: 'Complément d’adresse'},
        {key: 'zipcode', label: 'Code postal'}, {key: 'city', label: 'Ville'},
        {key: 'country', label: 'Pays'}, {key: 'tva', label: 'N° de TVA'}, {key: 'siren', label: 'SIREN'}
    ];
    readonly contactFields = [
        {key: 'firstname', label: 'Prénom *'},
        {key: 'name', label: 'Nom *'}, {key: 'fonction', label: 'Fonction'},
        {key: 'email', label: 'Email *'}, {key: 'phone', label: 'Téléphone'}
    ];

    constructor(private fb: FormBuilder, private api: ConfiguratorProviderService,
                private confirmation: ConfirmationService,
                @Optional() private route?: ActivatedRoute,
                @Optional() private router?: Router) {}

    get remunerations(): FormArray { return this.form.get('remunerations') as FormArray; }
    get leadRemunerations(): FormArray { return this.form.get('leadRemunerations') as FormArray; }

    ngOnInit(): void {
        if (!this.provider) {
            this.datastore = this.route?.snapshot.paramMap.get('tenant');
            const id = this.route?.snapshot.paramMap.get('id');
            if (!this.datastore) {
                this.error = 'Groupe du fournisseur introuvable.';
                return;
            }
            if (id) {
                this.description = 'Modification d’un fournisseur';
                this.breadcrumb[2].label = 'Édition';
                this.api.getProviderUsingGET(this.datastore, id).subscribe({
                    next: provider => {
                        this.provider = provider;
                        this.initializeForm();
                    },
                    error: () => this.error = 'Impossible de charger le fournisseur.'
                });
                return;
            }
            this.provider = {code: '', name: '', products: [], remunerations: []};
        }
        this.initializeForm();
    }

    private initializeForm(): void {
        this.selectedProducts = [...(this.provider.products || [])];
        this.selectedLeadProducts = Array.from(new Set((this.provider.remunerations || [])
            .filter(rule => (rule as any).typeTransaction === 'lead')
            .map(rule => rule.productType)));
        const civility = this.civilities.some(option => option.code === this.provider.contact?.civility)
            ? this.provider.contact.civility : null;
        const fields: Record<string, any> = {};
        this.addressFields.forEach(field => fields[field.key] = [this.provider[field.key] || '']);
        this.form = this.fb.group({
            ...fields,
            code: [{value: this.provider.code || '', disabled: !!this.provider.id},
                [Validators.required, Validators.minLength(2), Validators.maxLength(10), Validators.pattern(/\S/)]],
            name: [this.provider.name || '', [Validators.required, Validators.pattern(/\S/)]],
            description: [this.provider.description || ''],
            vatValue: [this.provider.vatValue ?? null, [Validators.required, Validators.min(0), Validators.max(50)]],
            priceDisplayMode: [this.provider.priceDisplayMode ?? null, Validators.required],
            language: [this.provider.language || 'fr', Validators.required],
            products: [this.selectedProducts, Validators.required],
            leadProducts: [this.selectedLeadProducts],
            hasContact: [!!this.provider.contact],
            contact: this.fb.group({
                civility: [civility, Validators.required],
                name: [this.provider.contact?.name || '', [Validators.required, Validators.pattern(/\S/)]],
                firstname: [this.provider.contact?.firstname || '', [Validators.required, Validators.pattern(/\S/)]],
                fonction: [this.provider.contact?.fonction || ''],
                email: [this.provider.contact?.email || '', [Validators.required, Validators.email]],
                phone: [this.provider.contact?.phone || '']
            }),
            remunerations: this.fb.array([
                RemunerationAccordionComponent.buildCompanyProviderGroup(this.fb, this.provider.code,
                    this.provider.remunerations || [], this.selectedProducts, 'command')
            ]),
            leadRemunerations: this.fb.array([
                RemunerationAccordionComponent.buildCompanyProviderGroup(this.fb, this.provider.code,
                    this.provider.remunerations || [], this.selectedLeadProducts, 'lead')
            ])
        });
        this.toggleContact(!!this.provider.contact);
        this.form.get('hasContact').valueChanges.subscribe(value => this.toggleContact(value));
        this.form.get('code').valueChanges.subscribe(code => {
            const group = this.remunerations.at(0) as FormGroup;
            group.get('provider').setValue(code);
            RemunerationAccordionComponent.updateProviderOnRules(group, code);
            const leadGroup = this.leadRemunerations.at(0) as FormGroup;
            leadGroup.get('provider').setValue(code);
            RemunerationAccordionComponent.updateProviderOnRules(leadGroup, code);
        });
        this.form.get('products').valueChanges.subscribe(products => this.changeProducts(products || []));
        this.form.get('leadProducts').valueChanges.subscribe(products => this.changeLeadProducts(products || []));
    }

    private toggleContact(enabled: boolean): void {
        if (enabled) { this.form.get('contact').enable(); }
        else { this.form.get('contact').disable(); }
    }

    private changeProducts(products: ProductTypeEnum[]): void {
        const removed = this.selectedProducts.filter(product => !products.includes(product));
        const previous = [...this.selectedProducts];
        const apply = () => {
            this.syncProducts(this.remunerations, products, 'command');
            this.selectedProducts = [...products];
            this.form.get('products').setValue([...products], {emitEvent: false});
            const leadProducts = this.selectedLeadProducts.filter(product => products.includes(product));
            this.syncProducts(this.leadRemunerations, leadProducts, 'lead');
            this.selectedLeadProducts = leadProducts;
            this.form.get('leadProducts').setValue([...leadProducts], {emitEvent: false});
        };
        if (removed.length) {
            this.form.get('products').setValue(previous, {emitEvent: false});
            this.confirmation.confirm({
                key: 'provider-products', header: 'Retirer des produits',
                message: 'Les rémunérations de ces produits seront retirées de cette fiche. Les accords déjà enregistrés chez les clients seront conservés.',
                acceptLabel: 'Retirer', rejectLabel: 'Annuler', accept: apply
            });
        } else { apply(); }
    }

    private changeLeadProducts(products: ProductTypeEnum[]): void {
        const selected = Array.from(new Set(products));
        const removed = this.selectedLeadProducts.some(product => !selected.includes(product));
        const previous = [...this.selectedLeadProducts];
        const apply = () => {
            const availableProducts = Array.from(new Set([...this.selectedProducts, ...selected]));
            if (availableProducts.length !== this.selectedProducts.length) {
                this.form.get('products').setValue(availableProducts);
            }
            this.syncProducts(this.leadRemunerations, selected, 'lead');
            this.selectedLeadProducts = [...selected];
            this.form.get('leadProducts').setValue([...selected], {emitEvent: false});
        };
        if (removed) {
            this.form.get('leadProducts').setValue(previous, {emitEvent: false});
            this.confirmation.confirm({
                key: 'provider-products', header: 'Retirer des produits Leads',
                message: 'Les rémunérations Leads de ces produits seront retirées de la fiche fournisseur.',
                acceptLabel: 'Retirer', rejectLabel: 'Annuler', accept: apply
            });
        } else { apply(); }
    }

    private syncProducts(
        remunerations: FormArray, products: ProductTypeEnum[], transactionType: 'command' | 'lead'
    ): void {
        const group = remunerations.at(0) as FormGroup;
        const current = group.get('products') as FormArray;
        const additions = RemunerationAccordionComponent.buildCompanyProviderGroup(
            this.fb, this.form.getRawValue().code, [], products, transactionType).get('products') as FormArray;
        // Keep pending edits for products that remain selected.
        group.setControl('products', this.fb.array(products.map((product, index) =>
            current.controls.find(control => control.get('productType').value === product)
                || additions.at(index)), transactionType === 'command' ? Validators.required : []));
    }

    save(): void {
        this.submitted = true;
        this.form.markAllAsTouched();
        if (this.form.invalid || this.saving) { return; }
        const value = this.form.getRawValue();
        const {hasContact, leadProducts, leadRemunerations, ...fields} = value;
        const payload: ProviderDTO = {
            ...this.provider, ...fields,
            contact: hasContact ? value.contact : null,
            remunerations: [
                ...configurationRemunerations(value.remunerations, 'command'),
                ...configurationRemunerations(leadRemunerations, 'lead')
            ]
        };
        this.saving = true;
        this.error = '';
        const request = this.provider.id
            ? this.api.updateProviderUsingPUT(this.datastore, payload)
            : this.api.createProviderUsingPOST(this.datastore, payload);
        request.subscribe({
            next: result => {
                this.saving = false;
                this.router?.navigate(['/h/settings/provider'], {state: {savedProviderName: result.name}});
            },
            error: error => {
                this.saving = false;
                this.error = error.error?.detail || error.error?.title || 'Impossible d’enregistrer le fournisseur.';
            }
        });
    }

    cancel(): void {
        this.router?.navigate(['/h/settings/provider']);
    }
}
